import React, { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import api from "../lib/api";

/**
 * SpotifyNowPlayingTile — listener-side card that surfaces the host's
 * currently-playing Spotify track on /radio/station/{slug}.
 *
 * Renders only when the host has a Spotify track on the on-air deck
 * (xfader < 0.5 → Deck A, ≥ 0.5 → Deck B). Polls the host session
 * every 8s (cheap GET, public route).
 *
 * The "Listen along on Spotify" CTA opens the official Spotify track
 * page (open.spotify.com/track/{id}) in a new tab. This nudges free
 * listeners toward Spotify Premium so they can hear the host's mix in
 * full — Spotify pays referral revenue when listeners convert and it
 * gives our music-discovery moments a path to value.
 */
export default function SpotifyNowPlayingTile({ slug }) {
  const [sp, setSp] = useState(null);

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    const tick = async () => {
      // Pause polling when tab is hidden to save battery.
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const { data } = await api.get(`/radio/host/session/${slug}`);
        if (cancelled) return;
        if (!data?.is_live) { setSp(null); return; }
        const xfader = typeof data.xfader === "number" ? data.xfader : 0.5;
        const onAir = xfader < 0.5 ? data.current_deck_a : data.current_deck_b;
        const sf = onAir?.spotify;
        if (sf?.uri && sf?.id) setSp({ ...sf, playing: !!onAir?.playing });
        else setSp(null);
      } catch { /* ignore — listener-side polling is best-effort */ }
    };
    tick();
    const id = setInterval(tick, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, [slug]);

  if (!sp) return null;
  const trackUrl = `https://open.spotify.com/track/${sp.id}`;

  return (
    <section
      data-testid="radio-station-spotify-now-playing"
      className="rounded-xl border border-emerald-500/40 bg-gradient-to-r from-emerald-900/30 via-black/40 to-black/40 p-3 mb-4"
    >
      <header className="flex items-center gap-2 mb-2">
        <svg viewBox="0 0 24 24" className="w-4 h-4 text-emerald-400 fill-current">
          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.479-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.78-.179-.9-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z" />
        </svg>
        <h3 className="text-[11px] uppercase tracking-[0.25em] font-black text-emerald-200">Now playing on Spotify</h3>
        {sp.playing && (
          <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-white/60">
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" /> live
          </span>
        )}
      </header>
      <div className="flex items-center gap-3">
        {sp.cover_url ? (
          <img
            data-testid="spotify-now-playing-cover"
            src={sp.cover_url}
            alt=""
            className="w-14 h-14 rounded shrink-0 object-cover"
          />
        ) : (
          <div className="w-14 h-14 rounded bg-emerald-900/40 grid place-items-center shrink-0">
            <svg viewBox="0 0 24 24" className="w-6 h-6 text-emerald-300/60 fill-current">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0z" />
            </svg>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div data-testid="spotify-now-playing-title" className="text-white font-display font-black text-base leading-tight truncate">
            {sp.title}
          </div>
          <div className="text-white/65 text-[12px] truncate">{sp.artist}</div>
          {sp.album && <div className="text-white/40 text-[10px] truncate italic">{sp.album}</div>}
        </div>
        <a
          data-testid="spotify-now-playing-open"
          href={trackUrl}
          target="_blank"
          rel="noreferrer"
          className="bg-emerald-500 hover:bg-emerald-400 text-black font-black text-[11px] px-3 py-2 rounded inline-flex items-center gap-1.5 shrink-0"
          title="Open on Spotify — listen along in full (Premium plays the full track, free plays a preview)."
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Listen along
        </a>
      </div>
      <p className="text-[10px] text-white/45 mt-2 leading-snug">
        The host is monitoring this track via Spotify. Tap to listen along — Spotify Premium plays the full song, free accounts get a preview.
      </p>
    </section>
  );
}
